import { useEffect, useState } from 'react';
import { Check, Copy, Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import {
    collectPwaDiagnostics,
    formatDiagnostics,
    requestPersistentStorage,
    type PwaDiagnostics,
} from '../../lib/pwaDiagnostics';

/**
 * Rendered at the bottom of the Troubleshooting article. Gives a user a way to
 * hand us the state of their device in one paste, which is what we need to tell
 * apart the several possible causes of the repeated-sign-in reports.
 */

const Row = ({ label, value, warn }: { label: string; value: string; warn?: boolean }) => (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-line last:border-0">
        <span className="text-[13px] text-fg-3 shrink-0">{label}</span>
        <span className={`text-[13px] font-mono text-right break-all ${warn ? 'text-danger' : 'text-fg'}`}>
            {value}
        </span>
    </div>
);

export const DiagnosticsPanel = () => {
    const [diagnostics, setDiagnostics] = useState<PwaDiagnostics | null>(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [persistResult, setPersistResult] = useState<boolean | null>(null);

    const refresh = async () => {
        setLoading(true);
        setDiagnostics(await collectPwaDiagnostics());
        setLoading(false);
    };

    useEffect(() => {
        let active = true;
        collectPwaDiagnostics().then((d) => {
            if (!active) return;
            setDiagnostics(d);
            setLoading(false);
        });
        return () => { active = false; };
    }, []);

    const handleCopy = async () => {
        if (!diagnostics) return;
        try {
            await navigator.clipboard.writeText(formatDiagnostics(diagnostics));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard blocked (older iOS, insecure context) — the readout is
            // on screen and can be selected by hand, so this is not fatal.
        }
    };

    const handlePersist = async () => {
        setPersistResult(await requestPersistentStorage());
        refresh();
    };

    return (
        <section className="mt-10 rounded-xl border border-line bg-panel overflow-hidden">
            <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-line">
                <div>
                    <h3 className="text-sm font-semibold text-fg">Diagnostics</h3>
                    <p className="text-[12px] text-fg-3">What this device is doing right now.</p>
                </div>
                <button
                    onClick={refresh}
                    aria-label="Refresh diagnostics"
                    className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-fg-3 hover:text-fg hover:bg-card transition-colors"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </header>

            <div className="px-4 py-2">
                {loading && !diagnostics ? (
                    <div className="flex items-center gap-2 py-6 text-fg-3 text-sm">
                        <Loader2 className="w-4 h-4 animate-spin" /> Checking…
                    </div>
                ) : diagnostics && (
                    <>
                        <Row label="Installed app" value={diagnostics.isStandalone ? 'yes' : 'no (browser tab)'} />
                        <Row label="Display mode" value={diagnostics.displayMode} />
                        <Row
                            label="Signed in"
                            value={diagnostics.hasSession ? 'yes' : 'no'}
                            warn={!diagnostics.hasSession}
                        />
                        <Row label="Sign-in method" value={diagnostics.signedInWith ?? '—'} />
                        <Row
                            label="Session stored"
                            value={diagnostics.hasSupabaseStorageKeys ? 'yes' : 'no'}
                            warn={!diagnostics.hasSupabaseStorageKeys}
                        />
                        <Row
                            label="Storage writable"
                            value={diagnostics.localStorageWritable ? 'yes' : 'no'}
                            warn={!diagnostics.localStorageWritable}
                        />
                        <Row
                            label="Storage kept"
                            value={diagnostics.storagePersisted === null ? 'unknown' : diagnostics.storagePersisted ? 'yes' : 'no'}
                            warn={diagnostics.storagePersisted === false}
                        />
                        <Row
                            label="Session expires in"
                            value={diagnostics.sessionExpiresInMinutes !== null ? `${diagnostics.sessionExpiresInMinutes} min` : '—'}
                        />
                        <Row label="Refresh token" value={diagnostics.hasRefreshToken ? 'yes' : 'no'} warn={diagnostics.hasSession && !diagnostics.hasRefreshToken} />
                        <Row
                            label="Pending invite"
                            value={diagnostics.pendingInvite ? 'present' : 'none'}
                            warn={!!diagnostics.pendingInvite && diagnostics.hasSession}
                        />
                        <Row label="Offline cache" value={diagnostics.serviceWorkerControlled ? 'active' : 'inactive'} />
                        {/* First question of any deploy incident: is this browser
                            on the current build, or has a cached service-worker
                            shell pinned it to an old one? */}
                        <Row label="Build" value={diagnostics.buildCommit ?? 'unknown'} />
                    </>
                )}
            </div>

            <footer className="flex flex-col sm:flex-row gap-2 px-4 py-3 border-t border-line">
                <button
                    onClick={handleCopy}
                    disabled={!diagnostics}
                    className="flex-1 flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold fd-btn-primary transition-all active:scale-[0.98] disabled:opacity-40"
                >
                    {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy diagnostics</>}
                </button>
                <button
                    onClick={handlePersist}
                    disabled={diagnostics?.storagePersisted === true}
                    className="flex-1 flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-medium border border-line text-fg-2 hover:bg-card transition-colors disabled:opacity-40"
                >
                    <ShieldCheck className="w-4 h-4" />
                    {diagnostics?.storagePersisted === true
                        ? 'Storage already kept'
                        : persistResult === false ? 'Request declined' : 'Ask browser to keep me signed in'}
                </button>
            </footer>
        </section>
    );
};
